import React, { useState, useEffect, useCallback, useRef } from 'react';
import jsQR from 'jsqr';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../services/api';
import Header from '../../components/common/Header';
import NotificationsPanel from '../../components/common/NotificationsPanel';
import DoctorBottomNav from '../../components/doctor/DoctorBottomNav';
import ScannerInput    from '../../components/doctor/ScannerInput';
import Avatar from '../../components/common/Avatar';
import { InlineLoader } from '../../components/common/Loader';
import {
  getDoctorAppointments,
  idCheckin,
  qrCheckin,
  offlineBook,
  checkOfflinePatient,
  getAvailableSlots,
  getAllSlotsToday,
  getOfflineAppointments,
  updateAppointmentStatus,
  getDoctorQR,
} from '../../services/doctorService';
import { useAuth } from '../../context/AuthContext';
import { useAppSettings } from '../../context/AppSettingsContext';
import { useSocket } from '../../context/SocketContext';
import { useDoctorNotifications } from '../../hooks/useDoctorNotifications';
import { formatTime12, todayISO, statusLabel } from '../../utils/helpers';
import { STATUS_COLORS, badgeStyle, rowBg, rowBorder } from '../../utils/statusColors';


// ── Support Links (uses admin settings) ───────────────────────────────────
function SupportLinks() {
  const settings = useAppSettings();
  const items = [
    { icon:'fa-brands fa-whatsapp', label:'WhatsApp Support', color:'text-green-500', href: settings.whatsappSupportNumber ? `https://wa.me/91${settings.whatsappSupportNumber}` : null },
    { icon:'fa-solid fa-phone',     label:'Call Support',     color:'text-primary',   href: settings.callSupportNumber    ? `tel:${settings.callSupportNumber}` : null },
  ];
  return (
    <>
      {items.map(item => item.href ? (
        <a key={item.label} href={item.href} target={item.href.startsWith('http') ? '_blank' : '_self'} rel="noopener noreferrer"
          className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-surface text-left transition-colors">
          <i className={`${item.icon} ${item.color} w-5 text-base`} />
          <span className="text-sm text-text-primary">{item.label}</span>
        </a>
      ) : (
        <div key={item.label} className="w-full flex items-center gap-3 px-3 py-3 rounded-xl opacity-50 cursor-not-allowed">
          <i className={`${item.icon} ${item.color} w-5 text-base`} />
          <span className="text-sm text-text-primary">{item.label}</span>
          <span className="ml-auto text-[9px] text-text-muted">Not set</span>
        </div>
      ))}
    </>
  );
}

// ── Doctor Sidebar ─────────────────────────────────────────────────────────
function DoctorSidebar({ isOpen, onClose }) {
  const navigate = useNavigate();
  const { user, role, logout } = useAuth();
  const [qr, setQR]               = React.useState(null);
  const [showQR, setShowQR]       = React.useState(false);
  const [qrLoaded, setQrLoaded]   = React.useState(false);
  const [showScanner, setShowScanner] = React.useState(false);

  const isStaff = role === 'staff';
  const perms   = user?.permissions || {};

  const loadQR = React.useCallback(() => {
    if (qrLoaded) { setShowQR(true); return; }
    getDoctorQR()
      .then(res => { setQR(res); setQrLoaded(true); setShowQR(true); })
      .catch(() => import('react-hot-toast').then(m => m.default.error('Could not load QR')));
  }, [qrLoaded]);

  // All possible quick-access items with their permission keys
  const ALL_NAV = [
    { icon:'fa-user-pen',           label:'Edit Profile',      path:'/doctor/profile',    doctorOnly: true },
    { icon:'fa-calendar-alt',       label:'Appointments',      path:'/doctor/appointments', perm:'appointments' },
    { icon:'fa-clock',              label:'Manage Slots',      path:'/doctor/slots',       perm:'slotManagement' },
    { icon:'fa-comment-dots',       label:'Chat',              path:'/doctor/chats',       perm:'chat' },
    { icon:'fa-users',              label:'Staff Accounts',    path:'/doctor/staff',       doctorOnly: true },
    { icon:'fa-indian-rupee-sign',  label:'Earnings',          path:'/doctor/earnings',    doctorOnly: true },
  ];

  const navItems = ALL_NAV.filter(item => {
    if (item.doctorOnly) return !isStaff;
    if (item.perm)       return isStaff ? (perms[item.perm] ?? false) : true;
    return true;
  });

  if (!isOpen) return null;
  return (
    <>
      <div className="backdrop" onClick={onClose} />
      <aside className="fixed top-0 left-0 bottom-0 w-72 bg-white z-50 shadow-xl flex flex-col overflow-y-auto">
        {/* Header */}
        <div className="px-5 py-5 border-b border-border bg-primary-light flex-shrink-0">
          <div className="flex items-center gap-3">
            <Avatar name={user?.name} size="md" />
            <div className="flex-1 min-w-0">
              <p className="font-bold text-text-primary truncate">{user?.name || (isStaff ? 'Staff' : 'Doctor')}</p>
              <p className="text-xs text-text-secondary truncate">
                {isStaff ? <span className="text-amber-600 font-semibold">Staff Account</span> : (user?.clinicName || '')}
              </p>
            </div>
            {/* QR button — doctor only */}
            {!isStaff && (
              <button
                onClick={loadQR}
                className="w-9 h-9 bg-white rounded-xl border border-primary/30 flex items-center justify-center flex-shrink-0 hover:bg-primary/5 active:scale-95 transition-all"
                title="My QR Code">
                <i className="fa-solid fa-qrcode text-primary text-base" />
              </button>
            )}
            <button onClick={onClose} className="btn-icon flex-shrink-0">
              <i className="fa-solid fa-xmark text-text-secondary" />
            </button>
          </div>
        </div>

        {/* QR Modal inside sidebar — doctor only */}
        {showQR && !isStaff && (
          <div className="absolute inset-0 z-10 bg-white flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <p className="font-bold text-text-primary">My Profile QR</p>
              <button onClick={() => setShowQR(false)} className="btn-icon">
                <i className="fa-solid fa-arrow-left text-text-secondary" />
              </button>
            </div>
            <div className="flex-1 flex flex-col items-center justify-center px-6 gap-4">
              {qr?.qrBase64 ? (
                <>
                  <div className="p-4 bg-white border-2 border-primary/20 rounded-3xl shadow-md">
                    <img src={qr.qrBase64} alt="QR Code" className="w-48 h-48" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-bold text-text-primary">{user?.name}</p>
                    <p className="text-xs text-text-muted mt-0.5">Scan to book appointment</p>
                  </div>
                  <div className="flex gap-3 w-full">
                    <button
                      onClick={() => {
                        const a = document.createElement('a');
                        a.href = qr.qrBase64;
                        a.download = 'faristaa-qr.png';
                        a.click();
                      }}
                      className="flex-1 py-3 bg-primary text-white rounded-2xl font-bold text-sm flex items-center justify-center gap-2">
                      <i className="fa-solid fa-download" />Download
                    </button>
                    <button
                      onClick={async () => {
                        if (navigator.share) {
                          try { await navigator.share({ title: 'Book my appointment', url: qr.profileUrl }); } catch {}
                        } else {
                          navigator.clipboard.writeText(qr.profileUrl);
                          import('react-hot-toast').then(m => m.default.success('Link copied!'));
                        }
                      }}
                      className="flex-1 py-3 border-2 border-primary text-primary rounded-2xl font-bold text-sm flex items-center justify-center gap-2">
                      <i className="fa-solid fa-share-nodes" />Share
                    </button>
                  </div>
                  <p className="text-[10px] text-text-muted text-center break-all px-2">{qr.profileUrl}</p>
                </>
              ) : (
                <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
              )}
            </div>
          </div>
        )}

        {/* Physical QR Scanner — for doctor or staff with qrScanner permission */}
        {(!isStaff || perms.qrScanner) && (
          <div className="px-4 pt-4 flex-shrink-0">
            <button
              onClick={() => setShowScanner(p => !p)}
              className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl border-2 transition-all text-left ${
                showScanner
                  ? 'border-primary bg-primary/5 text-primary'
                  : 'border-border hover:bg-surface text-text-primary'
              }`}>
              <i className={`fa-solid fa-barcode w-5 ${showScanner ? 'text-primary' : 'text-primary'}`} />
              <div className="flex-1">
                <span className="text-sm font-semibold">Physical QR Scanner</span>
                <p className="text-[10px] text-text-muted">USB · Bluetooth · Any device</p>
              </div>
              <i className={`fa-solid fa-chevron-${showScanner ? 'up' : 'down'} text-xs text-text-muted`} />
            </button>
            {showScanner && (
              <div className="mt-2 mb-2">
                <ScannerInput onCheckinSuccess={() => { onClose(); }} />
              </div>
            )}
          </div>
        )}

        {/* Quick Access nav — filtered by permissions */}
        <div className="px-4 pt-4 flex-1">
          <p className="text-[10px] font-semibold text-text-muted uppercase tracking-widest mb-2 px-1">Quick Access</p>
          {navItems.map(item => (
            <button key={item.path} onClick={() => { navigate(item.path); onClose(); }}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-surface text-left transition-colors">
              <i className={`fa-solid ${item.icon} text-primary w-5`} />
              <span className="text-sm text-text-primary">{item.label}</span>
            </button>
          ))}
        </div>

        <div className="px-4 pb-2 border-t border-border pt-3 flex-shrink-0">
          <p className="text-[10px] font-semibold text-text-muted uppercase tracking-widest mb-2 px-1">Support</p>
          <SupportLinks />
        </div>
        <div className="px-4 pb-6 pt-2 flex-shrink-0">
          <button onClick={() => { logout(); navigate('/doctor/login'); onClose(); }}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-red-50 text-danger font-semibold text-sm">
            <i className="fa-solid fa-right-from-bracket text-base" />Logout
          </button>
        </div>
      </aside>
    </>
  );
}


// ── Appointment Status Donut (CSS only) ────────────────────────────────────
function StatusDonut({ stats }) {
  const total = (stats.total || 0);
  if (total === 0) return null;

  // Uses STATUS_COLORS — same hex as stat cards → perfect visual consistency
  const segments = [
    { key:'completed',   label: STATUS_COLORS.completed['label'],    color: STATUS_COLORS.completed.hex,    val: stats.completed || 0 },
    { key:'checked-in',  label: STATUS_COLORS['checked-in']['label'], color: STATUS_COLORS['checked-in'].hex, val: stats.checkedIn  || 0 },
    { key:'confirmed',   label: STATUS_COLORS.confirmed['label'],    color: STATUS_COLORS.confirmed.hex,    val: stats.confirmed  || 0 },
    { key:'pending',     label: STATUS_COLORS.pending['label'],      color: STATUS_COLORS.pending.hex,      val: stats.pending    || 0 },
    { key:'cancelled',   label: STATUS_COLORS.cancelled['label'],    color: STATUS_COLORS.cancelled.hex,    val: stats.cancelled  || 0 },
  ].filter(s => s.val > 0);

  // Build conic-gradient
  let cumPct = 0;
  const parts = segments.map(s => {
    const pct = (s.val / total) * 100;
    const from = cumPct;
    cumPct += pct;
    return `${s.color} ${from.toFixed(1)}% ${cumPct.toFixed(1)}%`;
  });
  const gradient = `conic-gradient(${parts.join(', ')})`;

  return (
    <div className="card-p">
      <p className="text-sm font-bold text-text-primary mb-3">Today at a Glance</p>
      <div className="flex items-center gap-4">
        {/* Donut */}
        <div className="relative flex-shrink-0 w-20 h-20">
          <div className="w-20 h-20 rounded-full" style={{ background: gradient }} />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
              <span className="text-base font-black text-text-primary">{total}</span>
            </div>
          </div>
        </div>
        {/* Legend */}
        <div className="flex-1 grid grid-cols-2 gap-y-2 gap-x-3">
          {segments.map(s => (
            <div key={s.key} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: s.color }} />
              <div>
                <p className="text-[10px] text-text-muted leading-none">{s.label}</p>
                <p className="text-xs font-bold text-text-primary">{s.val}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Stat Card ──────────────────────────────────────────────────────────────
// ── Offline Booking Modal ──────────────────────────────────────────────────
function OfflineBookModal({ onClose, onBooked, doctorFee = 0 }) {
  // ── State ──────────────────────────────────────────────────────────────
  const [slots,        setSlots]        = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [step,         setStep]         = useState('slot');

  const [form,         setForm]         = useState({
    patientName: '', patientAge: '', patientGender: 'Male', address: '', notes: '',
    phone: '', fee: doctorFee,
  });
  const [otp,         setOtp]           = useState('');
  const [otpSending,  setOtpSending]    = useState(false);
  const [otpVerifying,setOtpVerifying]  = useState(false);
  const [otpError,    setOtpError]      = useState('');
  const [otpCooldown, setOtpCooldown]   = useState(0);
  const [loading,     setLoading]       = useState(false);
  const [booked,      setBooked]        = useState(null);
  const [phoneInfo,   setPhoneInfo]     = useState(null);
  const cooldownRef = React.useRef(null);
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Sync default fee
  useEffect(() => { if (doctorFee > 0) setF('fee', doctorFee); }, [doctorFee]);

  // Load today's slots
  useEffect(() => {
    getAllSlotsToday()
      .then(res => {
        const all = res.slots || [];
        setSlots(all);
        // Backend already filters expired/full slots — just pick first
        const next = all[0] || null;
        if (next) setSelectedSlot(next);
      })
      .catch(() => toast.error('Failed to load slots'))
      .finally(() => setLoadingSlots(false));
  }, []);

  // Available slots — IST-aware, check isActive + isExpired
  const availableSlots = React.useMemo(() => {
    const nowIST     = new Date(Date.now() + 5.5 * 60 * 60 * 1000);
    const nowMinutes = nowIST.getUTCHours() * 60 + nowIST.getUTCMinutes();
    return slots.filter(s => {
      if (!s.isActive)  return false;
      if (s.isExpired)  return false;
      if (s.bookedCount >= s.maxPatients) return false;
      const [h, m]     = s.startTime.split(':').map(Number);
      const endMinutes = h * 60 + m + (s.duration || 10);
      return endMinutes > nowMinutes;
    });
  }, [slots]);

  // Cooldown timer
  const startCooldown = () => {
    setOtpCooldown(60);
    clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setOtpCooldown(p => { if (p <= 1) { clearInterval(cooldownRef.current); return 0; } return p - 1; });
    }, 1000);
  };
  useEffect(() => () => clearInterval(cooldownRef.current), []);

  // Validate details form
  const validateDetails = () => {
    if (!form.phone.replace(/\D/g,'').match(/^\d{10}$/)) { toast.error('Enter valid 10-digit phone number'); return false; }
    if (!form.patientName.trim()) { toast.error('Patient name is required'); return false; }
    if (!form.patientAge || isNaN(form.patientAge) || form.patientAge < 1 || form.patientAge > 120) { toast.error('Enter valid age (1-120)'); return false; }
    if (!form.address.trim()) { toast.error('Patient address is required'); return false; }
    return true;
  };

  // Send OTP
  const handleSendOtp = async () => {
    if (!validateDetails()) return;
    setOtpSending(true); setOtpError('');
    try {
      const cleanPhone = form.phone.replace(/\D/g,'').slice(-10);
      await api.post('/auth/patient/send-otp', { phone: cleanPhone });
      // Also check if returning patient for discount
      checkOfflinePatient(cleanPhone).then(res => setPhoneInfo(res)).catch(() => {});
      setStep('otp');
      startCooldown();
      toast.success('OTP sent to ' + cleanPhone);
    } catch (err) {
      setOtpError(err.message || 'Failed to send OTP');
      toast.error(err.message || 'Failed to send OTP');
    } finally { setOtpSending(false); }
  };

  // Verify OTP + Book
  const handleVerifyAndBook = async () => {
    if (!otp || otp.length < 4) { toast.error('Enter the OTP'); return; }
    if (!selectedSlot) { toast.error('Please go back and select a slot'); return; }
    setOtpVerifying(true); setOtpError('');
    try {
      const cleanPhone = form.phone.replace(/\D/g,'').slice(-10);
      // Verify OTP first
      await api.post('/auth/patient/verify-otp-only', { phone: cleanPhone, otp });
      // Now book
      setLoading(true);
      const isOldPatient = phoneInfo?.isReturning || false;
      const res = await offlineBook({
        slotId:         selectedSlot._id,
        patientName:    form.patientName.trim(),
        patientAge:     form.patientAge,
        patientGender:  form.patientGender,
        isOldPatient,
        patientPhone:   cleanPhone,
        patientAddress: form.address.trim(),
        notes:          form.notes,
        fee:            parseFloat(form.fee) || doctorFee,
      });
      setBooked({ tokenNumber: res.tokenNumber, slotTime: res.slotTime, discountApplied: res.discountApplied || 0 });
      onBooked();
    } catch (err) {
      setOtpError(err.message || 'Invalid OTP');
      toast.error(err.message || 'Invalid OTP');
    } finally { setOtpVerifying(false); setLoading(false); }
  };

  const resetForm = () => {
    setBooked(null); setStep('slot'); setSelectedSlot(null); setOtp(''); setOtpError('');
    setPhoneInfo(null); setOtpCooldown(0);
    setForm({ patientName: '', patientAge: '', patientGender: 'Male', address: '', notes: '', phone: '', fee: doctorFee });
    // Reload slots to reflect fresh bookedCount
    setLoadingSlots(true);
    getAllSlotsToday()
      .then(res => { const s = res.slots || []; setSlots(s); if (s[0]) setSelectedSlot(s[0]); })
      .catch(() => {})
      .finally(() => setLoadingSlots(false));
  };

  // ── Success screen ──────────────────────────────────────────────────────
  if (booked) {
    return (
      <>
        <div className="backdrop" onClick={onClose} />
        <div className="bottom-sheet p-6 flex flex-col items-center gap-4">
          <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-1" />
          <div className="w-20 h-20 rounded-full bg-green-500 flex items-center justify-center shadow-lg shadow-green-500/30">
            <i className="fa-solid fa-check text-white text-4xl" />
          </div>
          <div className="text-center">
            <p className="text-xl font-black text-text-primary font-heading">Booking Confirmed!</p>
            <p className="text-sm text-text-muted mt-1">Walk-in patient added to today's queue</p>
          </div>
          <div className="w-full bg-primary-light border-2 border-primary/30 rounded-2xl px-6 py-5 text-center">
            <p className="text-xs text-text-muted font-semibold uppercase tracking-widest mb-1">Token Number</p>
            <p className="text-5xl font-black text-primary">#{booked.tokenNumber}</p>
            <p className="text-sm text-text-secondary mt-2 flex items-center justify-center gap-1.5">
              <i className="fa-solid fa-clock text-primary text-xs" />
              {formatTime12(booked.slotTime)}
            </p>
            {booked.discountApplied > 0 && (
              <p className="text-xs text-green-700 font-semibold mt-2 flex items-center justify-center gap-1">
                <i className="fa-solid fa-tag text-green-600 text-[10px]" />
                Returning patient discount ₹{booked.discountApplied} applied
              </p>
            )}
          </div>
          <button onClick={onClose} className="w-full btn-primary mt-1">
            <i className="fa-solid fa-check mr-2" />Done
          </button>
          <button onClick={resetForm} className="w-full py-2.5 text-sm text-text-muted font-medium">
            + Book Another Patient
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="backdrop" onClick={onClose} />
      <div className="bottom-sheet p-5 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto" />

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {step !== 'slot' && (
              <button onClick={() => setStep(step === 'otp' ? 'details' : 'slot')}
                className="w-8 h-8 rounded-xl bg-surface border border-border flex items-center justify-center">
                <i className="fa-solid fa-arrow-left text-text-secondary text-xs" />
              </button>
            )}
            <h3 className="text-base font-bold text-text-primary font-heading">
              <i className="fa-solid fa-person-walking text-primary mr-2" />Walk-in Booking
            </h3>
          </div>
          <button onClick={onClose} className="btn-icon">
            <i className="fa-solid fa-xmark text-text-secondary" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-1">
          {['Slot', 'Details', 'OTP'].map((label, i) => {
            const stepMap = ['slot', 'details', 'otp'];
            const idx     = stepMap.indexOf(step);
            const done    = i < idx;
            const active  = i === idx;
            return (
              <React.Fragment key={label}>
                {i > 0 && <div className={`flex-1 h-0.5 ${done ? 'bg-primary' : 'bg-border'}`} />}
                <div className={`flex items-center gap-1 ${active ? 'text-primary' : done ? 'text-primary' : 'text-text-muted'}`}>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                    done ? 'bg-primary text-white' : active ? 'bg-primary text-white' : 'bg-surface border border-border text-text-muted'
                  }`}>
                    {done ? <i className="fa-solid fa-check text-[8px]" /> : i + 1}
                  </div>
                  <span className="text-[10px] font-semibold">{label}</span>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {/* ── STEP 1: Slot ─────────────────────────────────────── */}
        {step === 'slot' && (
          <div className="space-y-4">
            <div>
              <label className="input-label flex items-center gap-1.5">
                <i className="fa-solid fa-clock text-primary text-xs" />
                Select Available Slot <span className="text-danger">*</span>
              </label>
              {loadingSlots ? <InlineLoader text="Loading slots..." /> : availableSlots.length === 0 ? (
                <div className="text-center py-6 bg-surface rounded-xl border border-border">
                  <i className="fa-solid fa-calendar-xmark text-3xl text-text-muted mb-2 block" />
                  <p className="text-sm font-semibold text-text-muted">No available slots right now</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2 max-h-44 overflow-y-auto">
                  {availableSlots.map(slot => {
                    const isSelected = selectedSlot?._id === slot._id;
                    const spotsLeft  = slot.maxPatients - slot.bookedCount;
                    return (
                      <button key={slot._id} onClick={() => setSelectedSlot(slot)}
                        className={`flex flex-col items-center py-3 px-1 rounded-xl border-2 transition-all ${
                          isSelected
                            ? 'border-primary bg-primary text-white shadow-md'
                            : 'border-border bg-white text-text-primary hover:border-primary/40'
                        }`}>
                        <span className="text-sm font-black">{formatTime12(slot.startTime)}</span>
                        <span className={`mt-0.5 text-[10px] font-semibold ${isSelected ? 'text-white/80' : 'text-success'}`}>
                          {spotsLeft} left
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
              {selectedSlot && (
                <p className="text-xs text-primary font-semibold mt-1.5 flex items-center gap-1">
                  <i className="fa-solid fa-circle-check" />
                  {formatTime12(selectedSlot.startTime)} · {selectedSlot.maxPatients - selectedSlot.bookedCount} spots left
                </p>
              )}
            </div>
            <button onClick={() => { if (!selectedSlot) { toast.error('Please select a slot'); return; } setStep('details'); }}
              disabled={!selectedSlot}
              className="btn-primary w-full">
              <i className="fa-solid fa-arrow-right mr-2" />Continue
            </button>
          </div>
        )}

        {/* ── STEP 2: Patient Details ───────────────────────────── */}
        {step === 'details' && (
          <div className="space-y-3">
            {/* Selected slot reminder */}
            <div className="flex items-center gap-2 p-2.5 bg-primary-light rounded-xl border border-primary/20">
              <i className="fa-solid fa-clock text-primary text-xs" />
              <p className="text-xs font-semibold text-primary">
                Slot: {formatTime12(selectedSlot?.startTime)} · {selectedSlot ? selectedSlot.maxPatients - selectedSlot.bookedCount : 0} spots left
              </p>
            </div>

            {/* Phone */}
            <div>
              <label className="input-label">Phone Number <span className="text-danger">*</span></label>
              <input type="tel" value={form.phone}
                onChange={e => setF('phone', e.target.value)}
                placeholder="10-digit mobile number" className="input" maxLength={15} />
              <p className="text-[10px] text-text-muted mt-1">OTP will be sent to verify this number</p>
            </div>

            {/* Name */}
            <div>
              <label className="input-label">Patient Name <span className="text-danger">*</span></label>
              <input value={form.patientName} onChange={e => setF('patientName', e.target.value)}
                placeholder="Full name" className="input" />
            </div>

            {/* Age + Gender */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="input-label">Age <span className="text-danger">*</span></label>
                <input type="number" min={1} max={120} value={form.patientAge}
                  onChange={e => setF('patientAge', e.target.value)}
                  placeholder="Age" className="input" />
              </div>
              <div>
                <label className="input-label">Gender <span className="text-danger">*</span></label>
                <select value={form.patientGender} onChange={e => setF('patientGender', e.target.value)} className="input">
                  <option>Male</option><option>Female</option><option>Other</option>
                </select>
              </div>
            </div>

            {/* Address */}
            <div>
              <label className="input-label">Address <span className="text-danger">*</span></label>
              <input value={form.address} onChange={e => setF('address', e.target.value)}
                placeholder="House no., Street, City" className="input" />
            </div>

            {/* Fee — default from doctor profile */}
            <div>
              <label className="input-label flex items-center gap-1.5">
                <i className="fa-solid fa-indian-rupee-sign text-primary text-xs" />
                Consultation Fee (₹)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted font-bold text-sm">₹</span>
                <input type="number" min={0} value={form.fee}
                  onChange={e => setF('fee', e.target.value)}
                  placeholder={String(doctorFee || 0)} className="input pl-8" />
              </div>
              <p className="text-[10px] text-text-muted mt-1">Default: your consultation fee. No GST or platform fee.</p>
            </div>

            {/* Notes */}
            <div>
              <label className="input-label">Notes (optional)</label>
              <textarea value={form.notes} onChange={e => setF('notes', e.target.value)}
                placeholder="Any notes about this visit..." rows={2} className="input resize-none" />
            </div>

            <button onClick={handleSendOtp} disabled={otpSending}
              className="btn-primary w-full">
              {otpSending
                ? <><i className="fa-solid fa-spinner animate-spin mr-2" />Sending OTP...</>
                : <><i className="fa-solid fa-mobile-screen-button mr-2" />Send OTP & Verify</>
              }
            </button>
          </div>
        )}

        {/* ── STEP 3: OTP Verification ──────────────────────────── */}
        {step === 'otp' && (
          <div className="space-y-4">
            {/* Info */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl text-center">
              <i className="fa-solid fa-mobile-screen-button text-blue-500 text-2xl mb-2 block" />
              <p className="text-sm font-bold text-blue-800">OTP sent to</p>
              <p className="text-base font-black text-blue-700 mt-0.5">+91 {form.phone.replace(/\D/g,'').slice(-10)}</p>
              <p className="text-xs text-blue-500 mt-1">Enter the 6-digit OTP below</p>
            </div>

            {/* Returning patient window expired — has history but discount invalid */}
            {phoneInfo?.isReturning && phoneInfo?.windowExpired && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-clock text-amber-500 text-sm flex-shrink-0" />
                  <p className="text-xs font-bold text-amber-800">Returning Patient — Discount Expired</p>
                </div>
                <p className="text-xs text-amber-700 mt-1 leading-relaxed">
                  This patient visited before, but the discount window of {phoneInfo.validityMonths} month{phoneInfo.validityMonths !== 1 ? 's' : ''} has passed.
                  {phoneInfo.expiredDaysAgo ? ` Expired ${phoneInfo.expiredDaysAgo} day${phoneInfo.expiredDaysAgo !== 1 ? 's' : ''} ago.` : ''} Full fee applies.
                </p>
              </div>
            )}

            {/* Returning patient auto-discount info — show exact amount */}
            {phoneInfo?.isReturning && phoneInfo?.hasDiscount && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-xl space-y-1.5">
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-tag text-green-600 text-sm flex-shrink-0" />
                  <p className="text-xs text-green-700 font-semibold">Returning patient — discount applied</p>
                </div>
                <div className="grid grid-cols-3 gap-1 text-center text-[11px] mt-1">
                  <div className="bg-white rounded-lg py-1.5 border border-green-100">
                    <p className="text-text-muted">Base Fee</p>
                    <p className="font-bold text-text-primary">₹{parseFloat(form.fee) || 0}</p>
                  </div>
                  <div className="bg-white rounded-lg py-1.5 border border-green-100">
                    <p className="text-green-600">Discount</p>
                    <p className="font-bold text-green-700">−₹{phoneInfo.discountAmount || 0}</p>
                  </div>
                  <div className="bg-green-100 rounded-lg py-1.5 border border-green-200">
                    <p className="text-green-700">Payable</p>
                    <p className="font-bold text-green-800">₹{Math.max((parseFloat(form.fee) || 0) - (phoneInfo.discountAmount || 0), 0)}</p>
                  </div>
                </div>
              </div>
            )}

            {/* OTP input */}
            <div>
              <label className="input-label text-center">Enter OTP <span className="text-danger">*</span></label>
              <input
                type="number" value={otp} onChange={e => { setOtp(e.target.value); setOtpError(''); }}
                placeholder="• • • • • •" maxLength={6}
                className="input text-center text-2xl font-black tracking-widest"
                style={{ letterSpacing: '0.5em' }}
              />
              {otpError && <p className="text-xs text-danger mt-1 text-center">{otpError}</p>}
            </div>

            {/* Resend */}
            <div className="text-center">
              {otpCooldown > 0
                ? <p className="text-xs text-text-muted">Resend OTP in {otpCooldown}s</p>
                : <button onClick={handleSendOtp} disabled={otpSending}
                    className="text-xs text-primary font-semibold underline">
                    {otpSending ? 'Sending...' : 'Resend OTP'}
                  </button>
              }
            </div>

            <button onClick={handleVerifyAndBook} disabled={otpVerifying || loading || !otp}
              className="btn-primary w-full">
              {otpVerifying || loading
                ? <><i className="fa-solid fa-spinner animate-spin mr-2" />Verifying...</>
                : <><i className="fa-solid fa-bolt mr-2" />Verify & Confirm Booking</>
              }
            </button>
            <p className="text-center text-xs text-text-muted">
              Fee: ₹{parseFloat(form.fee) || doctorFee} · Token auto-assigned
            </p>
          </div>
        )}
      </div>
    </>
  );
}


// ── Check-in Result Card ───────────────────────────────────────────────────
function PatientResultCard({ patient, appointment, onScanAnother, onDone }) {
  return (
    <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-3">
      {/* Success header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-success flex items-center justify-center flex-shrink-0">
          <i className="fa-solid fa-check text-white text-lg" />
        </div>
        <div>
          <p className="font-bold text-green-700">Check-in Successful!</p>
          <p className="text-xs text-green-600">Token #{appointment?.tokenNumber} · Patient is checked in</p>
        </div>
      </div>

      {/* Patient detail card */}
      <div className="bg-white rounded-xl p-3 space-y-2">
        <div className="flex items-center gap-3">
          <Avatar src={patient?.profileImg} name={patient?.name} size="md" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-text-primary">{patient?.name}</p>
            <p className="text-xs text-text-muted">
              {patient?.age && `Age: ${patient.age}`}{patient?.gender ? ` · ${patient.gender}` : ''}
            </p>
            <p className="text-xs text-text-muted">{patient?.phone}</p>
          </div>
          {/* Status pill */}
          <span className="flex-shrink-0 flex items-center gap-1 bg-green-100 text-green-700 text-[10px] font-bold px-2 py-1 rounded-full">
            <i className="fa-solid fa-hospital-user text-[9px]" />In Clinic
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs text-text-secondary border-t border-border pt-2">
          <div><span className="text-text-muted">Time:</span> <span className="font-medium">{formatTime12(appointment?.slotTime)}</span></div>
          <div><span className="text-text-muted">Token:</span> <span className="font-medium">#{appointment?.tokenNumber}</span></div>
          <div><span className="text-text-muted">Type:</span> <span className="font-medium capitalize">{appointment?.type || 'clinic'}</span></div>
          <div><span className="text-text-muted">Status:</span> <span className="font-medium text-success">Checked In</span></div>
        </div>
        {/* Patients list under this appointment */}
        {appointment?.patients?.length > 0 && (
          <div className="border-t border-border pt-2">
            <p className="text-[10px] text-text-muted font-semibold uppercase tracking-wide mb-1.5">Patients</p>
            {appointment.patients.map((p, i) => (
              <div key={i} className="flex items-center gap-2 text-xs py-0.5">
                <span className="w-4 h-4 bg-primary-light text-primary rounded-full flex items-center justify-center text-[9px] font-bold">{i+1}</span>
                <span className="font-medium">{p.name}</span>
                <span className="text-text-muted">{p.age}y · {p.gender}</span>
                <span className={`ml-auto text-[9px] font-medium ${p.isOldPatient ? 'text-success' : 'text-text-muted'}`}>{p.isOldPatient ? 'Old' : 'New'}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex gap-2">
        {/* Done — closes modal, refreshes dashboard, triggers next process */}
        <button
          onClick={() => onDone?.()}
          className="flex-1 py-2.5 bg-success text-white text-sm font-bold rounded-xl flex items-center justify-center gap-2">
          <i className="fa-solid fa-circle-check" />Done
        </button>
        {/* Scan another — stays in scanner for next patient */}
        <button
          onClick={onScanAnother}
          className="flex-1 py-2.5 text-sm font-semibold text-primary border border-primary rounded-xl flex items-center justify-center gap-2">
          <i className="fa-solid fa-qrcode" />Next Patient
        </button>
      </div>
    </div>
  );
}

function QRScannerModal({ onClose, onSuccess, onDone }) {
  const [mode, setMode]         = useState('qr');
  const [bookingId, setBookingId] = useState('');
  const [loading, setLoading]   = useState(false);
  const [result, setResult]     = useState(null);
  const [error, setError]       = useState('');
  const [camError, setCamError] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanPct, setScanPct]   = useState(0);
  const [fileLoading, setFileLoading] = useState(false);

  const videoRef    = useRef(null);
  const streamRef   = useRef(null);
  const canvasRef   = useRef(null);
  const rafRef      = useRef(null);
  const activeRef   = useRef(true);
  const fileInputRef = useRef(null);

  const stopCamera = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    setScanning(false);
  }, []);

  const doCheckin = useCallback(async (rawValue) => {
    if (!activeRef.current) return;
    activeRef.current = false;
    cancelAnimationFrame(rafRef.current);
    stopCamera();
    setScanning(false);
    setLoading(true);
    const trimmed = rawValue.trim();
    try {
      // Determine if this is a JWT QR token (patient appointment QR)
      // or a plain bookingId (manual entry or QR code containing just an ID)
      const isJWT = trimmed.split('.').length === 3 && !trimmed.includes(' ');
      let res;
      if (isJWT) {
        // Patient appointment QR — use qrCheckin (validates JWT + marks check-in)
        res = await qrCheckin(trimmed);
      } else {
        // Manual bookingId entry or file-uploaded QR with plain bookingId
        let id = trimmed;
        try { const p = JSON.parse(trimmed); id = p.bookingId || trimmed; } catch {}
        res = await idCheckin(id);
      }
      setResult({ valid: true, patient: res.patient, appointment: res.appointment });
      onSuccess?.();
    } catch (err) {
      setResult({ valid: false, reason: err.response?.data?.error || err.message });
    } finally { setLoading(false); }
  }, [stopCamera, onSuccess]);

  // Animate scan line
  useEffect(() => {
    if (!scanning) return;
    let pct = 0, dir = 1;
    const id = setInterval(() => {
      pct += dir * 3;
      if (pct >= 100) { pct = 100; dir = -1; }
      if (pct <= 0)   { pct = 0;   dir =  1; }
      setScanPct(pct);
    }, 20);
    return () => clearInterval(id);
  }, [scanning]);

  const startScanLoop = useCallback(() => {
    const tick = () => {
      if (!activeRef.current) return;
      const video  = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas || video.readyState < 2) {
        rafRef.current = requestAnimationFrame(tick); return;
      }
      canvas.width  = video.videoWidth  || 640;
      canvas.height = video.videoHeight || 480;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      if (window.BarcodeDetector) {
        new window.BarcodeDetector({ formats: ['qr_code'] })
          .detect(canvas)
          .then(codes => {
            if (!activeRef.current) return;
            if (codes.length > 0) doCheckin(codes[0].rawValue);
            else rafRef.current = requestAnimationFrame(tick);
          })
          .catch(() => {
            const code = jsQR(imageData.data, canvas.width, canvas.height, { inversionAttempts: 'attemptBoth' });
            if (code?.data) doCheckin(code.data);
            else rafRef.current = requestAnimationFrame(tick);
          });
        return;
      }
      const code = jsQR(imageData.data, canvas.width, canvas.height, { inversionAttempts: 'attemptBoth' });
      if (code?.data) doCheckin(code.data);
      else rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [doCheckin]);

  const startCamera = useCallback(async () => {
    activeRef.current = true;
    setCamError(''); setScanning(false); setResult(null);

    if (!navigator.mediaDevices?.getUserMedia) {
      setCamError('Camera API not available. Use Booking ID or Upload QR image.');
      setMode('id'); return;
    }
    let stream = null, lastErr = null;
    for (const c of [
      { video: { facingMode: { exact: 'environment' } } },
      { video: { facingMode: 'environment' } },
      { video: { facingMode: 'user' } },
      { video: true },
    ]) {
      try { stream = await navigator.mediaDevices.getUserMedia(c); break; }
      catch (err) {
        lastErr = err;
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          setCamError('Camera permission denied. Use Booking ID or Upload QR image instead.'); return;
        }
      }
    }
    if (!stream) {
      if (lastErr?.name === 'NotFoundError') setCamError('No camera found on this device.');
      else if (lastErr?.name === 'NotReadableError') setCamError('Camera in use by another app.');
      else { setMode('id'); return; }
      return;
    }
    streamRef.current = stream;
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current.muted = true;
      try { await videoRef.current.play(); } catch {}
    }
    setScanning(true);
    startScanLoop();
  }, [startScanLoop]); // stopCamera is stable (empty dep array)

  useEffect(() => {
    if (mode === 'qr') startCamera();
    else { activeRef.current = false; stopCamera(); }
    return () => { activeRef.current = false; stopCamera(); };
  }, [mode]); // eslint-disable-line

  const handleIdCheckin = async () => {
    if (!bookingId.trim()) { setError('Enter a booking ID'); return; }
    setError(''); setLoading(true);
    try {
      const res = await idCheckin(bookingId.trim());
      setResult({ valid: true, patient: res.patient, appointment: res.appointment });
      onSuccess?.();
    } catch (err) {
      setResult({ valid: false, reason: err.response?.data?.error || err.message });
    } finally { setLoading(false); }
  };

  // File upload: decode QR from image using jsQR npm package
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setFileLoading(true);
    try {
      const raw = await new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
          URL.revokeObjectURL(url);
          const cv = document.createElement('canvas');
          cv.width = img.width; cv.height = img.height;
          const ctx = cv.getContext('2d');
          ctx.drawImage(img, 0, 0);
          const d = ctx.getImageData(0, 0, cv.width, cv.height);
          const code = jsQR(d.data, cv.width, cv.height, { inversionAttempts: 'attemptBoth' });
          if (code?.data) resolve(code.data);
          else reject(new Error('No QR code found in this image. Make sure the QR is clear and not cropped.'));
        };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not load image file.')); };
        img.src = url;
      });
      activeRef.current = true;
      doCheckin(raw);
    } catch (err) {
      setResult({ valid: false, reason: err.message });
    } finally { setFileLoading(false); }
  };

  const reset = () => {
    setResult(null); setBookingId(''); setError('');
    activeRef.current = true;
    if (mode === 'qr') startCamera();
  };

  return (
    <>
      <div className="backdrop" onClick={() => { activeRef.current = false; stopCamera(); onClose(); }} />
      <div className="bottom-sheet p-5 space-y-4 max-h-[92vh] overflow-y-auto">
        <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto" />
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-text-primary font-heading">
            <i className="fa-solid fa-user-check text-primary mr-2" />Check-in Patient
          </h3>
          <button onClick={() => { activeRef.current = false; stopCamera(); onClose(); }} className="btn-icon">
            <i className="fa-solid fa-xmark text-text-secondary" />
          </button>
        </div>

        {!result && (
          <div className="flex bg-surface rounded-xl p-1 border border-border">
            {[['id','Booking ID','fa-keyboard'],['qr','Camera Scan','fa-qrcode']].map(([key,label,icon]) => (
              <button key={key} onClick={() => setMode(key)}
                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-semibold transition-all ${
                  mode === key ? 'bg-white text-primary shadow-sm' : 'text-text-muted'
                }`}>
                <i className={`fa-solid ${icon} text-sm`} />{label}
              </button>
            ))}
          </div>
        )}

        {result ? (
          result.valid ? (
            <PatientResultCard
              patient={result.patient}
              appointment={result.appointment}
              onScanAnother={reset}
              onDone={() => {
                // Done: close modal, reload dashboard, fire success callback
                onSuccess?.();
                onDone?.(result.appointment);
                onClose();
              }}
            />
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-danger flex items-center justify-center flex-shrink-0">
                  <i className="fa-solid fa-xmark text-white text-lg" />
                </div>
                <div>
                  <p className="font-bold text-red-700">Check-in Failed</p>
                  <p className="text-xs text-red-600 mt-0.5">{result.reason}</p>
                </div>
              </div>
              <button onClick={reset} className="w-full py-2.5 text-sm font-bold text-danger border border-danger rounded-xl">
                Try Again
              </button>
            </div>
          )
        ) : mode === 'id' ? (
          <div className="space-y-3">
            <div>
              <label className="input-label">Booking ID</label>
              <input value={bookingId}
                onChange={e => { setBookingId(e.target.value); setError(''); }}
                onKeyDown={e => e.key === 'Enter' && handleIdCheckin()}
                placeholder="FARISTAA-BKG-2026-... or FARISTAA-OFF-..."
                className={`input font-mono text-sm ${error ? 'border-danger' : ''}`} autoFocus />
              {error && <p className="text-xs text-danger mt-1">{error}</p>}
            </div>
            <button onClick={handleIdCheckin} disabled={loading} className="btn-primary">
              {loading
                ? <span className="flex items-center justify-center gap-2"><i className="fa-solid fa-spinner animate-spin" />Checking...</span>
                : <span><i className="fa-solid fa-user-check mr-2" />Check In</span>}
            </button>

            {/* File upload option in ID mode too */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs text-text-muted">or</span>
              <div className="flex-1 h-px bg-border" />
            </div>
            <button onClick={() => fileInputRef.current?.click()} disabled={fileLoading}
              className="w-full flex items-center justify-center gap-2 py-2.5 border border-border text-text-secondary rounded-xl text-sm font-semibold hover:bg-surface disabled:opacity-50">
              {fileLoading
                ? <><i className="fa-solid fa-spinner animate-spin" /> Reading QR...</>
                : <><i className="fa-solid fa-image" /> Upload QR Code Image</>}
            </button>
            <p className="text-xs text-text-muted text-center -mt-1">Upload patient QR code from file or gallery</p>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />
          </div>
        ) : (
          <div className="space-y-3">
            {loading && (
              <div className="flex items-center justify-center gap-3 py-6">
                <i className="fa-solid fa-spinner animate-spin text-primary text-2xl" />
                <span className="text-sm font-semibold text-text-primary">Verifying check-in...</span>
              </div>
            )}
            {!loading && camError ? (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center space-y-3">
                <i className="fa-solid fa-camera-slash text-amber-500 text-3xl" />
                <p className="text-sm font-semibold text-amber-800">Camera Unavailable</p>
                <p className="text-xs text-amber-600 leading-relaxed">{camError}</p>
                <button onClick={() => { setCamError(''); startCamera(); }}
                  className="w-full py-2.5 bg-amber-500 text-white rounded-xl text-sm font-bold">
                  <i className="fa-solid fa-rotate mr-2" />Try Again
                </button>
                <button onClick={() => setMode('id')}
                  className="w-full py-2.5 border border-amber-300 text-amber-700 rounded-xl text-sm font-semibold">
                  <i className="fa-solid fa-keyboard mr-2" />Enter Booking ID
                </button>
              </div>
            ) : !loading && (
              <>
                {/* Camera view */}
                <div className="relative w-full bg-black rounded-2xl overflow-hidden" style={{ aspectRatio:'1/1' }}>
                  <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
                  <canvas ref={canvasRef} className="hidden" />
                  {/* Scanning overlay */}
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="relative w-52 h-52">
                        {['top-0 left-0 border-t-[3px] border-l-[3px]',
                          'top-0 right-0 border-t-[3px] border-r-[3px]',
                          'bottom-0 left-0 border-b-[3px] border-l-[3px]',
                          'bottom-0 right-0 border-b-[3px] border-r-[3px]',
                        ].map((cls, i) => (
                          <div key={i} className={`absolute w-7 h-7 border-primary ${cls}`}
                            style={{ borderRadius: 2 }} />
                        ))}
                        {scanning && (
                          <div className="absolute left-0 right-0 h-[2px] bg-primary"
                            style={{
                              top: `${scanPct}%`,
                              boxShadow: '0 0 8px 2px rgba(13,148,136,0.8)',
                              transition: 'top 20ms linear',
                            }} />
                        )}
                      </div>
                    </div>
                    <div className="absolute bottom-3 left-0 right-0 text-center">
                      <span className="bg-black/70 text-white text-xs px-3 py-1.5 rounded-full font-medium">
                        {scanning ? '🔍 Scanning for QR code...' : 'Starting camera...'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* File upload option */}
                <button onClick={() => fileInputRef.current?.click()} disabled={fileLoading}
                  className="w-full flex items-center justify-center gap-2 py-2.5 border border-border text-text-secondary rounded-xl text-sm font-semibold hover:bg-surface disabled:opacity-50">
                  {fileLoading
                    ? <><i className="fa-solid fa-spinner animate-spin" /> Reading QR from image...</>
                    : <><i className="fa-solid fa-image" /> Upload QR Image from File</>}
                </button>
                <p className="text-xs text-text-muted text-center -mt-1">
                  Patient can also show QR from screenshot or gallery
                </p>
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />

                <button onClick={() => setMode('id')}
                  className="w-full py-2.5 border border-border text-text-secondary rounded-xl text-sm font-semibold">
                  <i className="fa-solid fa-keyboard mr-2" />Enter Booking ID instead
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ── Appointment Row ────────────────────────────────────────────────────────
function AppointmentRow({ appt, onCheckin, onComplete, onChat, isOffline }) {
  const patientName = isOffline
    ? (appt.patients?.[0]?.name || 'Walk-in Patient')
    : (appt.patientId?.name || 'Patient');

  // Row bg/border from centralized STATUS_COLORS
  const rowStyle = {
    background:   rowBg(appt.status),
    borderColor:  rowBorder(appt.status),
  };

  return (
    <div className="flex items-center gap-3 p-3 border rounded-xl transition-all" style={rowStyle}>
      <div className="relative flex-shrink-0">
        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
          style={{ background: isOffline ? STATUS_COLORS.offline.hex : STATUS_COLORS[appt.status]?.hex || '#0D9488' }}>
          #{appt.tokenNumber}
        </div>
        {isOffline && (
          <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-amber-400 rounded-full border border-white flex items-center justify-center">
            <i className="fa-solid fa-person-walking text-white" style={{fontSize:'6px'}} />
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-1">
          <p className="text-sm font-semibold text-text-primary truncate">{patientName}</p>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border"
            style={badgeStyle(appt.status)}>
            {statusLabel(appt.status)}
          </span>
        </div>
        <p className="text-xs text-text-muted">
          {formatTime12(appt.slotTime)}
          {isOffline && <span className="text-amber-600 font-medium"> · Walk-in</span>}
          {!isOffline && <span className={appt.type === 'video' ? ' text-info' : ' text-primary'}> · {appt.type === 'video' ? 'Video' : 'Clinic'}</span>}
          {appt.patients?.[0]?.age && ` · ${appt.patients[0].age}y`}
          {appt.patients?.[0]?.isOldPatient
            ? <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded-full ml-1"><i className="fa-solid fa-rotate-left text-[8px]" /> Returning</span>
            : appt.patients?.[0] && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-full ml-1"><i className="fa-solid fa-user-plus text-[8px]" /> New</span>
          }
        </p>
        {appt.notes && <p className="text-[10px] text-text-muted italic truncate mt-0.5">"{appt.notes}"</p>}
      </div>
      <div className="flex flex-col gap-1 flex-shrink-0">
        {['pending','confirmed'].includes(appt.status) && (
          isOffline
            // Walk-in: direct check-in button
            ? <button onClick={() => onCheckin(appt, true)}
                className="px-2 py-1.5 bg-amber-500 text-white text-[10px] font-semibold rounded-lg active:scale-95">
                <i className="fa-solid fa-user-check mr-1" />In
              </button>
            // Video: open chat directly
            : appt.type === 'video'
            ? <button onClick={() => onChat && onChat(appt)}
                className="px-2 py-1.5 bg-info text-white text-[10px] font-semibold rounded-lg active:scale-95">
                <i className="fa-solid fa-comment-dots mr-1" />Chat
              </button>
            // Clinic online: QR scan
            : <button onClick={() => onCheckin(appt, false)}
                className="px-2 py-1.5 bg-primary text-white text-[10px] font-semibold rounded-lg active:scale-95">
                <i className="fa-solid fa-qrcode mr-1" />Scan
              </button>
        )}
        {appt.status === 'checked-in' && (
          <button onClick={() => onComplete(appt._id, isOffline)}
            className="px-2 py-1.5 bg-success text-white text-[10px] font-semibold rounded-lg">
            <i className="fa-solid fa-circle-check mr-1" />Done
          </button>
        )}
      </div>
    </div>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────────────
export default function DoctorDashboard() {
  const navigate   = useNavigate();
  const { user, role } = useAuth();
  const { socket } = useSocket();
  const isStaff = role === 'staff';
  const perms   = user?.permissions || {};

  const [sidebarOpen, setSidebarOpen]     = useState(false);
  const [appointments, setAppts]          = useState([]);
  const [allTodaySlots, setAllTodaySlots] = useState([]); // all slots — available + expired
  const [offlineAppts, setOfflineAppts]   = useState([]);
  const [availableCount, setAvailableCount] = useState(0);
  const [stats, setStats]                 = useState({});
  const [loading, setLoading]             = useState(true);
  const [showQR, setShowQR]               = useState(false);
  const [showMyQR, setShowMyQR]           = useState(false);
  const [myQRData, setMyQRData]           = useState(null);
  const [myQRLoading, setMyQRLoading]     = useState(false);

  // Load doctor QR when modal opens
  useEffect(() => {
    if (!showMyQR || myQRData) return;
    setMyQRLoading(true);
    getDoctorQR()
      .then(res => setMyQRData(res))
      .catch(() => import('react-hot-toast').then(m => m.default.error('Could not load QR')))
      .finally(() => setMyQRLoading(false));
  }, [showMyQR, myQRData]);
  const [showOfflineBook, setShowOfflineBook] = useState(false);
  const { unreadCount: notifCount, notifications: doctorNotifs, markRead: markDoctorRead, deleteOne: deleteDocNotif, deleteAll: clearDocNotifs } = useDoctorNotifications();
  const [showNotifs, setShowNotifs] = useState(false);
  const [storeUnread, setStoreUnread]       = useState(0);
  const [filter, setFilter]               = useState('all'); // 'all' | 'online' | 'offline'
  const [statFilter, setStatFilter]         = useState('all'); // clickable stat cards

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Staff with only qrScanner permission cannot call appointments/offline/slots APIs
      // Only call the APIs that this user has access to
      const canSeeAppts = !isStaff || perms.appointments;

      const [onlineRes, offlineRes, slotsRes, allSlotsRes] = await Promise.all([
        canSeeAppts ? getDoctorAppointments({ date: todayISO(), limit: 50 }) : Promise.resolve({ appointments: [] }),
        canSeeAppts ? getOfflineAppointments({ date: todayISO() })          : Promise.resolve({ appointments: [] }),
        canSeeAppts ? getAvailableSlots({ date: todayISO() })               : Promise.resolve({ slots: [] }),
        canSeeAppts ? getAllSlotsToday()                                     : Promise.resolve({ slots: [] }),
      ]);

      const online  = onlineRes.appointments  || [];
      const offline = offlineRes.appointments || [];
      const allToday = [...online, ...offline];

      setAppts(online);
      setOfflineAppts(offline);
      setAvailableCount(slotsRes.slots?.length || 0);
      setAllTodaySlots(allSlotsRes.slots || []);

      setStats({
        total:     allToday.length,
        online:    online.length,
        offline:   offline.length,
        pending:   allToday.filter(a => a.status === 'pending').length,
        confirmed: allToday.filter(a => a.status === 'confirmed').length,
        checkedIn: allToday.filter(a => a.status === 'checked-in').length,
        completed: allToday.filter(a => a.status === 'completed').length,
        cancelled: allToday.filter(a => a.status === 'cancelled').length,
        // FIX: count ALL appointments by type (not just online) — clinic walk-ins are type='clinic' too
        video:     allToday.filter(a => a.type === 'video').length,
        clinic:    allToday.filter(a => a.type === 'clinic' || a.isOffline).length,
      });
    } catch (err) {
      // Only show error if it's not a permission error (staff with limited perms)
      if (err?.response?.status !== 403) {
        toast.error('Failed to load dashboard');
      }
    }
    finally { setLoading(false); }
  }, [isStaff, perms.appointments]);

  useEffect(() => { loadData(); }, [loadData]);

  // Reload appointments on new booking notification
  useEffect(() => {
    if (!socket) return;
    const onNotif = notif => {
      if (notif.type === 'new_booking') loadData();
    };
    socket.on('new-notification', onNotif);
    return () => { socket.off('new-notification', onNotif); };
  }, [socket, loadData]);

  const handleDirectCheckin = async (appt) => {
    // Offline patient direct check-in (no QR needed)
    try {
      await updateAppointmentStatus(appt._id, { status: 'checked-in' });
      toast.success(`Token #${appt.tokenNumber} checked in`);
      loadData();
    } catch (err) { toast.error(err.message); }
  };

  const handleComplete = async (apptId, isOffline) => {
    try {
      await updateAppointmentStatus(apptId, { status: 'completed' });
      toast.success(isOffline ? 'Walk-in completed! Earnings updated.' : 'Marked as completed');
      loadData();
    } catch (err) { toast.error(err.message); }
  };

  // Merge online + offline, deduplicate by _id, sort by token
  const allAppts = (() => {
    const seen = new Set();
    return [
      ...appointments.map(a => ({ ...a, _isOffline: false })),
      ...offlineAppts.map(a => ({ ...a, _isOffline: true })),
    ]
      .filter(a => { if (seen.has(a._id)) return false; seen.add(a._id); return true; })
      .sort((a, b) => a.tokenNumber - b.tokenNumber);
  })();

  // Apply both type filter and stat filter
  const filtered = (() => {
    let list = allAppts;
    // Type filter
    if (filter === 'online')  list = list.filter(a => !a._isOffline);
    if (filter === 'offline') list = list.filter(a =>  a._isOffline);
    // Stat filter (from clicking stat cards)
    if (statFilter === 'pending')    list = list.filter(a => a.status === 'pending');
    if (statFilter === 'confirmed')  list = list.filter(a => a.status === 'confirmed');
    if (statFilter === 'checked-in') list = list.filter(a => a.status === 'checked-in');
    if (statFilter === 'completed')  list = list.filter(a => a.status === 'completed');
    if (statFilter === 'cancelled')  list = list.filter(a => a.status === 'cancelled');
    if (statFilter === 'online')     list = list.filter(a => !a._isOffline);
    if (statFilter === 'offline')    list = list.filter(a =>  a._isOffline);
    if (statFilter === 'video')      list = list.filter(a => a.type === 'video');
    if (statFilter === 'clinic')     list = list.filter(a => a.type === 'clinic' && !a._isOffline);
    return list;
  })();

  return (
    <div className="page-container">
      <Header showLogo isDoctor onMenuClick={() => setSidebarOpen(true)} showNotif
        notifCount={notifCount} onNotifClick={() => { setShowNotifs(p => !p); if (!showNotifs) markDoctorRead(); }}
        />

      {showNotifs && (
        <NotificationsPanel
          notifications={doctorNotifs}
          unreadCount={notifCount}
          onClose={() => setShowNotifs(false)}
          onMarkAllRead={markDoctorRead}
          onDeleteOne={deleteDocNotif}
          onDeleteAll={clearDocNotifs}
          onTap={(notif) => {
            setShowNotifs(false);
            if (notif.type === 'new_message' && notif.data?.conversationId) {
              const isStore = notif.data?.isStore || notif.title?.includes('Medicine Store');
              if (isStore) {
                navigate(`/doctor/medicine-chat/${notif.data.conversationId}`);
              } else {
                // FIX: Staff ke liye conversationId mein doctorId hoga (patientId_doctorId)
                // Notification mein jo conversationId saved hai woh sender ke perspective se
                // bani thi — hume usse directly use karna chahiye, rebuild nahi karna
                navigate(`/doctor/chat/${notif.data.conversationId}`);
              }
            } else if (['new_booking','appointment_completed','checkin_confirmed','slot_delay'].includes(notif.type)) {
              navigate('/doctor/appointments');
            }
          }}
        />
      )}



      <DoctorSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* ── Floating Check-in Button — visible on ALL screen sizes ── */}
      {(role === 'doctor' || user?.permissions?.qrScanner) && (
        <button
          onClick={() => setShowQR(true)}
          className="fixed bottom-20 right-4 z-40 flex items-center gap-2 px-4 py-3 bg-primary text-white rounded-2xl shadow-lg shadow-primary/40 active:scale-95 transition-all md:bottom-6 md:right-6"
          title="Check-in Patient (QR / Booking ID)">
          <i className="fa-solid fa-qrcode text-lg" />
          <span className="text-sm font-bold hidden sm:inline">Check-in Patient</span>
        </button>
      )}

      <div className="px-4 pt-2 pb-24">
        {/* Welcome + Available button */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-xs text-text-muted">{isStaff ? 'Staff Account' : 'Good day,'}</p>
            <h2 className="text-lg font-bold text-text-primary font-heading truncate">{user?.name || (isStaff ? 'Staff' : 'Doctor')}</h2>
          </div>
          {(!isStaff || perms.appointments) && (<button
            onClick={() => setShowOfflineBook(true)}
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border transition-all bg-amber-500 text-white border-amber-500 shadow-sm active:scale-95">
            <i className="fa-solid fa-person-walking text-xs" />
            Walk-in
            {availableCount > 0 && (
              <span className="bg-white text-amber-600 text-[10px] font-bold px-1.5 py-0.5 rounded-full ml-0.5">
                {availableCount}
              </span>
            )}
          </button>)}
        </div>

      {/* Stats + appointments (hidden for staff without appointments perm) */}
        {/* ── Scrollable stats row (hidden for staff without appointments perm) ── */}
        {(!isStaff || perms.appointments) && <div className="flex gap-2.5 overflow-x-auto scrollbar-hide pb-1 mb-3 -mx-1 px-1">
          {[
            // ── All stat cards auto-derive colors from STATUS_COLORS ──────────────
            ...['all','pending','confirmed','checked-in','completed','cancelled','online','offline','video','clinic'].map(key => {
              const sc = STATUS_COLORS[key] || STATUS_COLORS.all;
              const valMap = {
                all:          stats.total     || 0,
                pending:      stats.pending   || 0,
                confirmed:    stats.confirmed || 0,
                'checked-in': stats.checkedIn || 0,
                completed:    stats.completed || 0,
                cancelled:    stats.cancelled || 0,
                online:       stats.online    || 0,
                offline:      stats.offline   || 0,
                video:        stats.video     || 0,
                clinic:       stats.clinic    || 0,
              };
              return { key, label: sc.label, value: valMap[key], icon: sc.faIcon, bg: sc.bg, color: sc.text, dot: sc.hex };
            }),
          ].map(s => {
            const isActive = statFilter === s.key;
            return (
              <button key={s.key}
                onClick={() => setStatFilter(isActive ? 'all' : s.key)}
                className="flex-shrink-0 flex flex-col items-center gap-1 px-3 py-2.5 rounded-2xl border-2 transition-all active:scale-95 min-w-[72px]"
                style={{
                  background:   isActive ? s.dot : s.bg,
                  borderColor:  isActive ? s.dot : 'transparent',
                }}>
                <i className={`fa-solid ${s.icon} text-base`}
                  style={{ color: isActive ? '#fff' : s.dot }} />
                <p className="text-lg font-black leading-none"
                  style={{ color: isActive ? '#fff' : s.color }}>{s.value}</p>
                <p className="text-[9px] font-semibold"
                  style={{ color: isActive ? 'rgba(255,255,255,0.85)' : s.color }}>{s.label}</p>
              </button>
            );
          })}
        </div>}

        {(!isStaff || perms.appointments) && !loading && stats.total > 0 && (
          <div className="mb-4">
            <StatusDonut stats={stats} />
          </div>
        )}

        {/* ── Medicine Store Chats shortcut ─────────────── */}
        {user?.medicineStaffName && (
          <button onClick={() => { setStoreUnread(0); navigate('/doctor/medicine-store-chats'); }}
            className="w-full mb-4 active:scale-98 transition-all">
            <div className="rounded-2xl border-2 border-green-200 bg-white overflow-hidden shadow-sm">
              {/* Top bar */}
              <div className="bg-gradient-to-r from-green-600 to-emerald-500 px-4 py-3 flex items-center gap-3">
                {/* Store icon - left */}
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0 border border-white/30">
                  <i className="fa-solid fa-store text-white text-xl" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-black text-white">{user?.medicineStaffName || 'Medicine Store'}</p>
                  <p className="text-[10px] text-white/80 font-medium">Patient Medicine Enquiries</p>
                </div>
                {storeUnread > 0 && (
                  <div className="flex flex-col items-center flex-shrink-0">
                    <span className="w-7 h-7 bg-white text-green-700 text-xs font-black rounded-full flex items-center justify-center shadow-sm">
                      {storeUnread > 9 ? '9+' : storeUnread}
                    </span>
                    <span className="text-[9px] text-white/80 mt-0.5">new</span>
                  </div>
                )}
              </div>
              {/* Bottom row */}
              <div className="px-4 py-2.5 flex items-center justify-between bg-green-50">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <i className="fa-solid fa-comment-dots text-green-600 text-xs" />
                    <span className="text-xs text-green-700 font-semibold">Chat</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="fa-solid fa-phone text-green-600 text-xs" />
                    <span className="text-xs text-green-700 font-semibold">Voice</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="fa-solid fa-video text-green-600 text-xs" />
                    <span className="text-xs text-green-700 font-semibold">Video</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-green-600">
                  <span className="text-xs font-bold">Open</span>
                  <i className="fa-solid fa-arrow-right text-xs" />
                </div>
              </div>
            </div>
          </button>
        )}

        {/* Quick offline book CTA if slots available — doctor OR appointments-staff */}
        {availableCount > 0 && (!isStaff || perms.appointments) && (
          <button onClick={() => setShowOfflineBook(true)}
            className="w-full mb-4 flex items-center justify-between px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
                <i className="fa-solid fa-user-plus text-amber-600" />
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-amber-800">Book Walk-in Patient</p>
                <p className="text-xs text-amber-600">{availableCount} slot{availableCount !== 1 ? 's' : ''} available today</p>
              </div>
            </div>
            <i className="fa-solid fa-arrow-right text-amber-500" />
          </button>
        )}

        {/* ── Today's Slot Status strip ─────────────────── */}
        {allTodaySlots.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-bold text-text-primary mb-2 flex items-center gap-1.5">
              <i className="fa-solid fa-clock-rotate-left text-primary text-[10px]" />
              Today&apos;s Slots
            </p>
            <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
              {allTodaySlots.map(slot => {
                const now       = new Date();
                const [h, m]    = slot.startTime.split(':').map(Number);
                const endMs     = new Date().setHours(h, m + (slot.duration || 10), 0, 0);
                const startMs   = new Date().setHours(h, m, 0, 0);
                const isExpired = endMs < now;
                const isLive    = startMs <= now && endMs >= now;
                const isFull    = slot.bookedCount >= slot.maxPatients;
                const remaining = slot.maxPatients - slot.bookedCount;
                return (
                  <div key={slot._id}
                    className={`flex-shrink-0 flex flex-col items-center px-3 py-2.5 rounded-2xl border-2 min-w-[72px] ${
                      isExpired ? 'bg-slate-50 border-slate-200 opacity-55'
                        : isLive ? 'bg-green-50 border-green-400 shadow-sm'
                        : isFull ? 'bg-red-50 border-red-200'
                        : 'bg-primary-light border-primary/30'
                    }`}>
                    <span className={`text-xs font-black ${
                      isExpired ? 'text-slate-400' : isLive ? 'text-green-700' : isFull ? 'text-danger' : 'text-primary'
                    }`}>{formatTime12(slot.startTime)}</span>
                    <span className={`text-[9px] font-bold mt-0.5 ${
                      isExpired ? 'text-slate-400' : isLive ? 'text-green-600' : isFull ? 'text-danger' : 'text-primary/80'
                    }`}>
                      {isExpired ? 'ENDED' : isLive ? '● LIVE' : isFull ? 'FULL' : `${remaining} left`}
                    </span>
                    <span className="text-[8px] text-text-muted mt-0.5">
                      {slot.type === 'video' ? 'Video' : 'Clinic'}
                    </span>
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] text-text-muted mt-1.5 flex gap-3">
              {(() => {
                const now = new Date();
                const exp = allTodaySlots.filter(s => {
                  const [h,m] = s.startTime.split(':').map(Number);
                  return new Date().setHours(h, m+(s.duration||10),0,0) < now;
                }).length;
                const live = allTodaySlots.filter(s => {
                  const [h,m] = s.startTime.split(':').map(Number);
                  return new Date().setHours(h,m,0,0) <= now && new Date().setHours(h,m+(s.duration||10),0,0) >= now;
                }).length;
                const up = allTodaySlots.length - exp - live;
                return (<>
                  {live > 0 && <span className="text-green-600 font-bold">● {live} live</span>}
                  {up   > 0 && <span className="text-primary font-semibold">{up} upcoming</span>}
                  {exp  > 0 && <span className="text-slate-400">{exp} ended · not bookable</span>}
                </>);
              })()}
            </p>
          </div>
        )}

        {/* Filter tabs + active stat badge */}
        <div className="flex items-center gap-2 mb-3">
          <div className="flex gap-1.5 flex-1">
            {[
              { key:'all',     label:'All' },
              { key:'online',  label:'Online' },
              { key:'offline', label:'Walk-in' },
            ].map(f => (
              <button key={f.key} onClick={() => setFilter(f.key)}
                className={`flex-1 py-2 rounded-xl text-xs font-bold border-2 transition-all ${
                  filter === f.key
                    ? f.key === 'offline' ? 'bg-amber-500 text-white border-amber-500' : 'bg-primary text-white border-primary'
                    : 'bg-white text-slate-500 border-border'
                }`}>
                {f.label}
              </button>
            ))}
          </div>
          {statFilter !== 'all' && (
            <button onClick={() => setStatFilter('all')}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold bg-blue-50 text-blue-700 border-2 border-blue-200 flex-shrink-0">
              <span className="capitalize">{statFilter}</span>
              <i className="fa-solid fa-xmark text-[10px]" />
            </button>
          )}
        </div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-bold text-text-primary">Appointments</p>
          <span className="text-xs text-text-muted">{filtered.length} shown</span>
        </div>

        {/* Appointments list (hidden for staff without appointments perm) */}
        {(!isStaff || perms.appointments) && <div className="space-y-2">
          {loading ? <InlineLoader text="Loading..." /> :
           filtered.length === 0 ? (
            <div className="text-center py-8">
              <i className="fa-solid fa-calendar-xmark text-3xl text-text-muted mb-2 block" />
              <p className="text-sm text-text-muted">No appointments yet</p>
              {availableCount > 0 && (
                <button onClick={() => setShowOfflineBook(true)}
                  className="mt-3 px-4 py-2 bg-amber-500 text-white text-xs font-semibold rounded-full">
                  + Book Walk-in Patient
                </button>
              )}
            </div>
          ) : filtered.map(appt => (
            <AppointmentRow
              key={appt._id}
              appt={appt}
              isOffline={appt._isOffline}
              onCheckin={(appt, isOff) => isOff ? handleDirectCheckin(appt) : setShowQR(true)}
              onComplete={handleComplete}
              onChat={(appt) => {
                // Video consultation → open chat with this patient
                const patientId = typeof appt.patientId === 'object' ? appt.patientId._id : appt.patientId;
                const doctorId  = typeof appt.doctorId  === 'object' ? appt.doctorId._id  : appt.doctorId;
                const convId    = [patientId, doctorId].sort().join('_');
                navigate(`/doctor/chat/${convId}`);
              }}
            />
          ))}
        </div>}

        {/* Staff access reminder — doctors only */}
        {!isStaff && <div className="mt-6 p-3 bg-surface border border-border rounded-xl flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-primary-light flex items-center justify-center flex-shrink-0">
            <i className="fa-solid fa-users text-primary text-sm" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-text-primary">Share dashboard with staff</p>
            <p className="text-[10px] text-text-muted">Create staff accounts with specific permissions</p>
          </div>
          <button onClick={() => navigate('/doctor/staff')}
            className="text-xs font-semibold text-primary flex-shrink-0">
            Manage →
          </button>
        </div>}
      </div>

      {showQR && (role === 'doctor' || user?.permissions?.qrScanner) && (
        <QRScannerModal
          onClose={() => setShowQR(false)}
          onSuccess={loadData}
          onDone={(appointment) => {
            // Reload dashboard so checked-in patient appears immediately
            loadData();
            setShowQR(false);
            // Emit socket event so patient gets real-time check-in notification
            if (socket) {
              socket.emit('checkin-done', {
                appointmentId: appointment?._id,
                patientId:     appointment?.patientId,
              });
            }
          }}
        />
      )}

      {/* My QR Code Modal (desktop) */}
      {showMyQR && (
        <>
          <div className="fixed inset-0 bg-black/60 z-50" onClick={() => setShowMyQR(false)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-white rounded-3xl shadow-2xl p-6 w-80 max-w-[90vw]">
            <div className="flex items-center justify-between mb-4">
              <p className="font-black text-text-primary font-heading">My Profile QR</p>
              <button onClick={() => setShowMyQR(false)} className="btn-icon">
                <i className="fa-solid fa-xmark text-text-secondary" />
              </button>
            </div>
            {myQRLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
              </div>
            ) : myQRData?.qrBase64 ? (
              <>
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-white border-2 border-primary/20 rounded-2xl shadow-sm">
                    <img src={myQRData.qrBase64} alt="QR" className="w-44 h-44" />
                  </div>
                </div>
                <p className="text-xs text-text-muted text-center mb-4">Patients scan this to visit your profile and book</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      const a = document.createElement('a');
                      a.href = myQRData.qrBase64;
                      a.download = 'faristaa-qr.png';
                      a.click();
                    }}
                    className="flex-1 py-2.5 bg-primary text-white rounded-xl font-bold text-sm flex items-center justify-center gap-1.5">
                    <i className="fa-solid fa-download text-xs" />Download
                  </button>
                  <button
                    onClick={async () => {
                      if (navigator.share) {
                        try { await navigator.share({ title: 'Book my appointment', url: myQRData.profileUrl }); } catch {}
                      } else {
                        navigator.clipboard.writeText(myQRData.profileUrl);
                        import('react-hot-toast').then(m => m.default.success('Profile link copied!'));
                      }
                    }}
                    className="flex-1 py-2.5 border-2 border-primary text-primary rounded-xl font-bold text-sm flex items-center justify-center gap-1.5">
                    <i className="fa-solid fa-share-nodes text-xs" />Share
                  </button>
                </div>
              </>
            ) : (
              <p className="text-center text-sm text-text-muted py-8">Could not load QR code</p>
            )}
          </div>
        </>
      )}

      {showOfflineBook && <OfflineBookModal onClose={() => setShowOfflineBook(false)} onBooked={loadData} doctorFee={user?.consultationFee || 0} />}

      <DoctorBottomNav notifCount={notifCount} />
    </div>
  );
}
